import React, { useState } from 'react';
import { Home, Package, Truck, CheckCircle2, Phone, Mail, ArrowRight, FileText, Shield, Globe, Users, Box } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import QuoteRequestForm from '../components/QuoteRequestForm';

const RelocationShippingCanadaToIndiaPage = () => {
  const { toast } = useToast();

  const benefits = [
    {
      icon: Home,
      title: "Complete Household Shipping",
      description: "Ship furniture, appliances, clothing, kitchenware, and all personal belongings from Canada to India with specialized relocation services."
    },
    {
      icon: Box,
      title: "Flexible Pricing Options",
      description: "Three pricing tiers to match your needs: Airport-to-airport from $7.99/kg (India, 100kg min), Bulk doorstep $8.49/kg (138kg min), or Standard doorstep $9.99/kg (23kg min)."
    },
    {
      icon: Truck,
      title: "Door-to-Door Service",
      description: "We handle pickup from your Canadian home and deliver directly to your new address in India with full tracking throughout the journey."
    },
    {
      icon: FileText,
      title: "Customs Documentation Support",
      description: "Expert guidance on Transfer of Residence (ToR) documentation, inventory lists, and all required paperwork for smooth Indian customs clearance."
    },
    {
      icon: Shield,
      title: "Safe & Secure Handling",
      description: "Professional packing recommendations, insurance options, and careful handling ensure your belongings arrive safely in India."
    },
    {
      icon: Globe,
      title: "Experienced in Relocation",
      description: "We've helped hundreds of families and individuals relocate from Canada to India with hassle-free international moving services."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Free Relocation Quote",
      description: "Contact us with details about your move. We'll assess your shipment volume and provide a customized quote with the best rates."
    },
    {
      number: 2,
      title: "Packing Guidance",
      description: "We provide expert advice on packing household items safely. Use proper boxes, padding, and labeling to protect your belongings during transit."
    },
    {
      number: 3,
      title: "Customs Documentation",
      description: "Our team helps prepare all necessary customs paperwork including Transfer of Residence documents, detailed inventory lists, and declarations."
    },
    {
      number: 4,
      title: "Pickup & Shipment",
      description: "Schedule a convenient pickup date. We collect your packed items, weigh and measure them, and dispatch via trusted carriers to India."
    },
    {
      number: 5,
      title: "Delivery in India",
      description: "Your belongings clear customs and are delivered directly to your new home in India. Track your shipment every step of the way."
    }
  ];

  const commonItems = [
    {
      category: "Furniture & Large Items",
      items: ['Chairs and tables', 'Mattresses and bed frames', 'Bookshelves and cabinets', 'Small appliances']
    },
    {
      category: "Clothing & Textiles",
      items: ['Clothing and shoes', 'Bed linens and towels', 'Curtains and drapes', 'Winter wear']
    },
    {
      category: "Kitchen & Household",
      items: ['Cookware and utensils', 'Dishes and glassware', 'Small kitchen appliances', 'Household decor']
    },
    {
      category: "Personal & Electronics",
      items: ['Books and documents', 'Personal electronics', 'Toys and games', 'Sports equipment']
    }
  ];

  const packingTips = [
    {
      title: "Use Sturdy Boxes",
      tip: "Invest in quality moving boxes in various sizes. Double-box fragile items and use bubble wrap or packing paper for cushioning."
    },
    {
      title: "Label Everything",
      tip: "Clearly label each box with contents and destination room. Create a detailed inventory list for customs documentation."
    },
    {
      title: "Disassemble When Possible",
      tip: "Take apart furniture to save space and reduce dimensional weight charges. Keep all screws and parts in labeled bags."
    },
    {
      title: "Protect Electronics",
      tip: "Use original packaging when available. Remove batteries and wrap items in anti-static materials. Check Indian voltage compatibility."
    },
    {
      title: "Vacuum Seal Clothing",
      tip: "Use vacuum-sealed bags for clothing and linens to minimize space and protect from moisture during ocean transit."
    },
    {
      title: "Avoid Prohibited Items",
      tip: "Don't pack liquids, flammables, perishables, weapons, or high-value jewelry. Check restricted items list before packing."
    }
  ];

  const faqs = [
    {
      question: "How much does relocation shipping from Canada to India cost?",
      answer: "We offer three pricing options: (1) Airport-to-airport at $7.99/kg (India only, 100kg minimum), (2) Bulk doorstep delivery at $8.49/kg (138kg minimum), or (3) Standard doorstep at $9.99/kg (23kg minimum). A typical household relocation (200-500kg) ranges from $1,600-$4,250 CAD depending on service level. Contact us for an accurate quote based on your shipment details."
    },
    {
      question: "How long does household shipping to India take?",
      answer: "Relocation shipments typically take 4-8 weeks for delivery to India. This includes transit time, customs clearance processing, and final delivery. Express options can reduce this to 2-3 weeks at higher rates. We provide tracking updates throughout the entire process so you always know where your belongings are."
    },
    {
      question: "What documents are needed for relocation to India?",
      answer: "Key documents include: (1) Passport copy, (2) Transfer of Residence certificate or employment letter, (3) Detailed inventory list with item values, (4) Bill of Lading, (5) Packing list. For returning Indian citizens, additional ToR documentation may be required. We provide comprehensive guidance on all required paperwork and assist with preparation."
    },
    {
      question: "Can I ship furniture and large appliances to India?",
      answer: "Yes! We handle furniture, appliances, and large household items. However, consider that large appliances may not be compatible with Indian electrical standards (230V vs 120V in Canada). Furniture should be disassembled to minimize dimensional weight charges. We recommend shipping items that are worth the cost versus purchasing new in India."
    },
    {
      question: "Are there customs duties on household goods shipped to India?",
      answer: "Indian citizens and certain visa holders relocating to India may qualify for duty exemptions under Transfer of Residence provisions if they've lived abroad for at least 2 years and meet specific criteria. Commercial goods and brand-new items may attract duties. Our team helps you understand eligibility and prepare proper documentation to minimize or avoid customs charges where applicable."
    },
    {
      question: "How should I pack my belongings for international relocation?",
      answer: "Use strong, properly-sized boxes. Wrap fragile items individually with bubble wrap. Disassemble furniture and keep hardware in labeled bags. Vacuum-seal clothing and linens. Create a detailed inventory with contents and approximate values. Label each box clearly. Avoid packing prohibited items like liquids, batteries, or flammables. We provide comprehensive packing guidance to help ensure safe transit."
    },
    {
      question: "Do you provide packing services or boxes?",
      answer: "While we don't provide full-service packing, we offer expert packing guidance and can recommend where to purchase quality packing materials. We provide detailed instructions on how to pack various household items safely for international shipping. For professional packing services, we can refer you to local moving companies in your area."
    },
    {
      question: "Can I track my relocation shipment to India?",
      answer: "Yes! All relocation shipments include full tracking capabilities. Once your belongings are dispatched, we provide tracking numbers and regular updates. You can monitor your shipment's progress from Canada through customs clearance in India to final delivery at your new home. We keep you informed every step of the way."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Relocation Shipping Canada to India | Household Moving – Shippzy</title>
        <meta name="description" content="Move from Canada to India with reliable relocation shipping. Ship personal belongings and household goods with door-to-door delivery support." />
        <meta name="keywords" content="relocation shipping Canada to India, household shipping India, moving Canada to India, international relocation India, ship belongings India, transfer of residence India" />
        <link rel="canonical" href="https://www.shippzy.ca/relocation-shipping-canada-to-india" />
        
        <meta property="og:title" content="Relocation Shipping Canada to India | Household Moving – Shippzy" />
        <meta property="og:description" content="Move from Canada to India with reliable relocation shipping. Ship household goods with door-to-door delivery and customs support." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/relocation-shipping-canada-to-india" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Relocation Shipping from Canada to India",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca"
            },
            "areaServed": ["CA", "IN"],
            "serviceType": "International Relocation and Household Shipping",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "7.99",
                "priceCurrency": "CAD",
                "unitText": "per kg"
              }
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                🏠 Trusted Relocation Services
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Relocation Shipping from Canada to India
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Moving from Canada to India? Shippzy provides comprehensive relocation shipping services for individuals and families. We handle everything from furniture and appliances to clothing and personal belongings with specialized care. Our door-to-door service includes pickup from your Canadian home, expert customs documentation support, and delivery directly to your new address anywhere in India. With bulk shipping discounts and experienced handling, we make international relocation simple and affordable.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Relocation Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Shippzy for Your Relocation to India</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized household shipping services designed for international moves from Canada to India
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* New Pricing Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">International Relocation & Bulk Shipping</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Flexible international shipping options for personal belongings, household goods and relocation shipments
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Airport-to-Airport */}
              <Card className="bg-white text-gray-900 hover:shadow-2xl transition-all">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl">Airport-to-Airport</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-red-600 mb-2">$7.99</div>
                    <div className="text-gray-600 text-sm">CAD per kg</div>
                    <div className="text-gray-500 text-sm mt-2">Minimum 100 kg</div>
                    <div className="text-gray-500 text-sm">India only</div>
                  </div>
                  <div className="space-y-2 text-left text-sm text-gray-600 mb-6">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Airport collection at destination</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Customer handles customs</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Most economical option</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bulk Doorstep */}
              <Card className="bg-white text-gray-900 hover:shadow-2xl transition-all border-2 border-red-500">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Truck className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl">Bulk Doorstep Delivery</CardTitle>
                  <div className="mt-2 inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">POPULAR</div>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-red-600 mb-2">$8.49</div>
                    <div className="text-gray-600 text-sm">CAD per kg</div>
                    <div className="text-gray-500 text-sm mt-2">Minimum 138 kg</div>
                  </div>
                  <div className="space-y-2 text-left text-sm text-gray-600 mb-6">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Doorstep delivery at destination</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>India, UK, Dubai/UAE, Nepal</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Best value for large shipments</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Standard Doorstep */}
              <Card className="bg-white text-gray-900 hover:shadow-2xl transition-all">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Home className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl">Doorstep Shipping</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-red-600 mb-2">$9.99</div>
                    <div className="text-gray-600 text-sm">CAD per kg</div>
                    <div className="text-gray-500 text-sm mt-2">Minimum 23 kg</div>
                  </div>
                  <div className="space-y-2 text-left text-sm text-gray-600 mb-6">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Doorstep delivery at destination</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>India, UK, Dubai/UAE, Nepal</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Lower minimum weight</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Button 
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 text-lg"
                onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Get a Quote
              </Button>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What You Can Ship When Relocating to India</h2>
              <p className="text-xl text-gray-600">We handle all types of household goods and personal belongings</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {commonItems.map((section, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Package className="w-6 h-6 text-red-600" />
                      {section.category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {section.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-8 bg-yellow-50 border-2 border-yellow-300">
              <CardContent className="pt-6">
                <p className="text-gray-800">
                  <strong>Important:</strong> Prohibited items include liquids, flammable materials, perishable foods, weapons, ammunition, narcotics, and items restricted by Indian customs. Large electrical appliances may not be compatible with Indian voltage standards. Contact us for a complete list of restricted items.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Packing Tips for International Relocation</h2>
              <p className="text-xl text-gray-600">Expert guidance to ensure your belongings arrive safely in India</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packingTips.map((item, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.tip}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Relocation Shipping Process</h2>
              <p className="text-xl text-gray-600">5 simple steps to move your belongings from Canada to India</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Customs Documentation Guidance</h2>
              <p className="text-xl text-gray-600">We help you prepare all required paperwork for smooth Indian customs clearance</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-red-600" />
                    Required Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Passport copy (all pages with stamps)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Transfer of Residence certificate or employment letter</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Detailed inventory list with item descriptions and values</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Bill of Lading and packing list</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Copy of visa or residence permit (if applicable)</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-red-600" />
                    Our Support Services
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Document checklist and preparation guidance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Help creating detailed inventory lists</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Transfer of Residence eligibility assessment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Coordination with customs agents in India</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Updates throughout customs clearance process</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Services</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/shipping-to-india-from-canada" className="text-red-600 hover:text-red-700 font-semibold underline">
                  General shipping to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/cheap-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Cheap shipping options
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-shipping-brampton" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Brampton shipping services
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-courier-toronto" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Toronto courier services
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Common questions about relocation shipping from Canada to India</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <QuoteRequestForm />
          </div>
        </section>

        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Start Your Relocation to India with Confidence</h3>
            <p className="text-gray-300 mb-6">
              Professional household shipping services from Canada to India. Door-to-door delivery, customs documentation support, and bulk discounts for large relocations. Track your belongings every step of the way.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Relocation Quote
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                View All Services
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RelocationShippingCanadaToIndiaPage;